import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { CsCardListInterfaceClass } from 'components/cs-card-list/cs-card-list-interface';
import { DestroyGuard } from 'services/browser-utils';
import { LoggingService } from 'services/logging.service';
import { throttle } from 'lodash-es';


/**
 * Interface for card components that can be managed by cs-card-list.
 *
 * There are two approaches to create a card component:
 *
 * 1. **Standalone card** (doesn't wrap cs-card):
 *    - Extend CsCardInterfaceClass
 *    - Provide itself: `providers: [{ provide: CsCardInterfaceClass, useExisting: MyComponent }]`
 *    - Implement all styling directly
 *    - Example: CsInfoCardComponent
 *
 * 2. **Wrapper card** (wraps a cs-card in template):
 *    - Extend CsCardInterfaceClass
 *    - Provide itself: `providers: [{ provide: CsCardInterfaceClass, useExisting: MyComponent }]`
 *    - Use @ViewChild(CsCardComponent) to get inner card reference
 *    - Forward updateLayout(), getCurrentCardHeight() to inner card
 *    - Examples: CsNutritionPlanCard, CsSupportCardComponent
 */
export interface CsCardInterface {
  elemRef: ElementRef;
  updateLayout(): void;
  getCurrentCardHeight(): number;
  getCardElement(): HTMLElement;
  setList?(list: CsCardListInterfaceClass): void;
}

/**
 * Base class for card components. Extend this class and provide it via:
 * `providers: [{ provide: CsCardInterfaceClass, useExisting: YourComponent }]`
 *
 * If wrapping a cs-card, use @ViewChild(CsCardComponent) and forward methods:
 * ```typescript
 * @ViewChild(CsCardComponent) card: CsCardComponent;
 *
 * override updateLayout(): void {
 *   this.card?.updateLayout();
 * }
 *
 * override getCurrentCardHeight(): number {
 *   return this.card ? this.card.getCurrentCardHeight() : 0;
 * }
 * ```
 */
export class CsCardInterfaceClass implements CsCardInterface {
  protected list: CsCardListInterfaceClass | null = null;

  constructor(public elemRef: ElementRef) {
    // auto-register on DOM element for parent discovery
    (this.elemRef.nativeElement as any).__csCardComponent = this;
  }

  updateLayout(): void { }
  getCurrentCardHeight(): number { return 0; }
  getCardElement(): HTMLElement { return this.elemRef?.nativeElement; }

  setList(list: CsCardListInterfaceClass): void {
    this.list = list;
  }
}

@Component({
    selector: 'cs-card',
    templateUrl: 'cs-card.html',
    providers: [{ provide: CsCardInterfaceClass, useExisting: CsCardComponent }],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsCardComponent extends CsCardInterfaceClass implements OnChanges, AfterViewInit, OnDestroy {
  private destroyGuard = new DestroyGuard();
  @ViewChild('cardBody') cardBody: ElementRef;
  @ViewChild('cardShape') cardShape: ElementRef;
  @ViewChild('bodyCollapsed') bodyCollapsed: ElementRef;
  @ViewChild('bodyExpanded') bodyExpanded: ElementRef;
  @ViewChild('bodyPlain') bodyPlain: ElementRef;
  @ViewChild('headerTop') headerTop: ElementRef;
  @ViewChild('cardTitle') cardTitle: ElementRef;
  @ViewChild('content') cardContent: ElementRef;

  @Input() foldable: boolean | 'custom';
  @Input() expanded: boolean = false;
  /** hide the fold arrow while the card is expanded (tapping the header still folds it), for a
   *  card that shows its own control in that corner instead (#4142, the workout exercise card) */
  @Input() hideFoldIconWhenExpanded = false;
  /** the fold arrow while collapsed; a finished compact exercise card points it down (#4142 review fixes) */
  @Input() collapsedFoldIcon = 'arrow-dropright';
  @Output() expandedChange = new EventEmitter<any>();
  @HostBinding('class.highlighted')
  @Input() highlighted: boolean = false;
  @Output() highlightedChange = new EventEmitter<boolean>();
  @Output() headerClick: EventEmitter<Event> = new EventEmitter();
  @Input() loading: boolean;

  loadingInternal: boolean = false;

  @HostBinding('class.animate') animate = false;

  private resizeObserver: ResizeObserver = undefined;
  private throttledResizeUpdate: any = undefined;
  private hasInitialized = false;
  private animationEnableTimer: any = undefined;
  // true while a re-measure is already booked for the next frame, so several measurements in one
  // frame share a single follow-up
  private followUpMeasurePending = false;

  constructor(
    elemRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private loggingService: LoggingService) {
    super(elemRef);
  }


  bodyHeightPlain: number;
  bodyHeightCollapsed: number;
  bodyHeightExpanded: number;
  bodyHeightCurrent: number;

  updateLayout() {
    this.updateHeights();
    this.cdr.markForCheck();
  }

  getCardElement() {
    return this.elemRef.nativeElement
  }

  getCurrentCardHeight() {
    return this.bodyHeightCurrent || 0;
  }

  currentClasses: { [key: string]: boolean } = {};

  getCardCssClasses() {
    this.currentClasses.expanded = this.expanded;
    this.currentClasses.highlighted = this.highlighted;

    this.currentClasses.foldable = !!this.foldable;
    this.currentClasses['user-foldable'] = this.foldable === true;

    this.currentClasses.animate = this.animate;

    return this.currentClasses;
  }

  onPlainViewClicked(e: Event) {
    this.headerClick.emit(e);
  }

  onHeaderClicked(e: Event) {
    this.enableAnimationsNow();
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
    this.headerClick.emit(e);
    this.updateHeights();
    e.stopPropagation();
    e.stopImmediatePropagation();
  }

  expand() {
    if (this.expanded) {
      return;
    }
    this.enableAnimationsNow();
    this.expanded = true;
    this.expandedChange.emit(this.expanded);
  }

  onUnfoldIconClicked(e: Event) {
    this.enableAnimationsNow();
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
    e.stopPropagation();

    // only update positions if inside cs-card-list
    if (this.list) {
      this.list.updatePositions();
    } else if (this.foldable) {
      this.loggingService.trackAbnormalError('cs-card: foldable card has no list in onUnfoldIconClicked');
    }
  }

  updateHeights() {
    if (!this.resizeObserver) {
      // throttle resize calculations to avoid layout thrashing
      // no manual animation tracking needed — card shape height transitions and
      // card-list transform transitions are captured by document.getAnimations()
      this.throttledResizeUpdate = throttle(() => {
        this.performHeightCalculation();
      }, 100);

      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          if (this.throttledResizeUpdate) {
            this.throttledResizeUpdate();
          }
        });
      });
      this.resizeObserver.observe(this.elemRef.nativeElement);
    }

    // always perform height calculation (guards inside handle invalid states)
    this.performHeightCalculation();
    this.scheduleFollowUpMeasure();
  }

  // a measurement taken in the middle of a render (projected content, the spinner/content swap and
  // conditionally-rendered parts not drawn yet) can come out short. the ResizeObserver cannot repair
  // that: it only reports a size that differs from its LAST report at a frame boundary, and the card
  // usually ends the frame at exactly the size it started it. so every direct measurement is
  // checked once more on the next frame (design-docs/typescript-patterns.md, "How a card in a
  // cs-card-list gets its height"). the follow-up measures directly, so it never books another one
  private scheduleFollowUpMeasure() {
    if (this.followUpMeasurePending) {
      return;
    }
    this.followUpMeasurePending = true;
    requestAnimationFrame(() => {
      this.followUpMeasurePending = false;
      if (this.destroyGuard.isDestroyed) return;
      this.performHeightCalculation();
    });
  }

  private performHeightCalculation() {
    if (this.bodyPlain) {
      this.bodyHeightPlain = this.bodyPlain.nativeElement.offsetHeight;
    }

    if (this.cardBody) {
      if (this.bodyCollapsed) {
        this.bodyHeightCollapsed = this.bodyCollapsed.nativeElement.offsetHeight;
        this.cardBody.nativeElement.style.setProperty('--collapsed-height', `${this.bodyHeightCollapsed}px`);
      } else {
        this.bodyHeightCollapsed = 0;
      }
      if (this.bodyExpanded) {
        this.bodyHeightExpanded = this.bodyExpanded.nativeElement.offsetHeight;
        this.cardBody.nativeElement.style.setProperty('--expanded-height', `${this.bodyHeightExpanded}px`);
      }
    }

    let height = 0;
    if (!this.animate && this.cardContent) {
      // skip measurement if card has no width yet (cs-card-list hasn't positioned it)
      // this prevents measuring wrong heights before layout is ready
      if (this.elemRef.nativeElement.offsetWidth === 0) {
        return;
      }
      height = this.cardContent.nativeElement.offsetHeight;
    } else {
      if (this.bodyPlain) {
        height = this.bodyHeightPlain;
      } else {
        height = this.expanded ? this.bodyHeightExpanded : this.bodyHeightCollapsed;
      }
    }

    if (!isFinite(height)) {
      return;
    }
    if (this.animate && this.headerTop) {
      height += (this.headerTop.nativeElement as HTMLElement).offsetHeight;
    }

    if (height > 0 && this.bodyHeightCurrent !== height) {
      this.bodyHeightCurrent = height;
      if (this.elemRef) {
        this.elemRef.nativeElement.style.setProperty('--current-height', `${height}`);
        this.elemRef.nativeElement.style.setProperty('--current-height-px', `${height}px`);
      }
      if (this.list) {
        this.list.updatePositions();
      } else if (this.foldable && this.hasInitialized) {
        this.loggingService.trackAbnormalError('cs-card: foldable card has no list in performHeightCalculation');
      }

      // if waiting for height to stabilize before enabling animations, reset the timer
      if (!this.animate && this.foldable && !this.expanded && this.hasInitialized) {
        this.scheduleAnimationEnable();
      }
    }
  }

  ngAfterViewInit(): void {
    this.updateHeights();

    // enable animation for foldable cards (for expand/collapse)
    if (this.foldable) {
      if (this.expanded) {
        // already expanded, enable animations immediately
        this.animate = true;
      } else {
        // wait for height to stabilize before enabling animations
        this.scheduleAnimationEnable();
      }
    }

    // mark as initialized after MutationObserver has had a chance to set the list
    requestAnimationFrame(() => {
      this.hasInitialized = true;
    });
  }

  timeoutId: any;

  ngOnChanges(changes: SimpleChanges) {
    void changes;
    if (changes.loading) {
      clearTimeout(this.timeoutId);

      if (this.loading) {
        this.timeoutId = setTimeout(() => {
          if (this.destroyGuard.isDestroyed) return;
          this.loadingInternal = true;
          this.cdr.markForCheck();
        }, 200);
      } else {
        this.loadingInternal = false;
        this.cdr.markForCheck();
      }
    }
    // if expanded changes after initialization, enable animations
    if (changes.expanded && this.hasInitialized && !changes.expanded.firstChange) {
      this.enableAnimationsNow();
    }
    // the projected content, and this card's own spinner/content swap, render AFTER ngOnChanges in
    // the same pass, so this measurement can come out short — e.g. a workout set that opened while
    // the card's `loading` flipped (#4142). updateHeights() re-measures on the next frame for that
    this.updateHeights();
  }

  // schedule animation enable after height stabilizes
  // each height change resets the timer to ensure animations only enable after content settles
  private scheduleAnimationEnable() {
    if (this.animationEnableTimer) {
      clearTimeout(this.animationEnableTimer);
    }

    this.animationEnableTimer = setTimeout(() => {
      if (this.destroyGuard.isDestroyed) return;
      this.animate = true;
      this.cdr.markForCheck();
    }, 150);
  }

  // enable animations immediately (for user-triggered expand/collapse)
  private enableAnimationsNow() {
    if (this.animationEnableTimer) {
      clearTimeout(this.animationEnableTimer);
      this.animationEnableTimer = undefined;
    }
    if (!this.animate) {
      this.animate = true;
    }
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.throttledResizeUpdate) {
      this.throttledResizeUpdate.cancel();
      this.throttledResizeUpdate = null;
    }
    if (this.animationEnableTimer) {
      clearTimeout(this.animationEnableTimer);
    }
  }


  hasClientSmallScreen() {
    return hasClientSmallScreen()
  }
}